import { gql, useMutation } from '@apollo/client'
import { useToast } from '@farol-ds/react'
import { useCallback, useContext, useEffect } from 'react'

import { useSendOneClickButtonEvents } from '../../hooks'
import { BoxLockContext } from '../context'
import { SubscribeButton, SubscribeButtonProps } from '../subscribe-button'
import {
  OneClickButton_OfferLockCardFragment,
  OneClickButton_ViewerFragment,
  OneClickBuyMutation,
  OneClickBuyMutationVariables,
} from './__generated__/one-click-button.graphql'
import { useOneClickSuccessExperiment } from './use-one-click-success-experiment'
import { useWaitSubscriptionUpdate } from './use-wait-subscription-update'

type OneClickButtonProps = {
  offerLockCard: OneClickButton_OfferLockCardFragment
  viewer: OneClickButton_ViewerFragment
  kind?: SubscribeButtonProps['kind']
  leftIcon?: SubscribeButtonProps['leftIcon']
  forceDisabled?: boolean
  customLabel?: string
}

export const ONE_CLICK_BUY_MUTATION = gql`
  mutation oneClickBuy($input: OneClickBuyInput!) {
    oneClickBuy(input: $input) {
      orderCode
      success
    }
  }
`

export function OneClickButton(props: OneClickButtonProps) {
  const { offerLockCard, viewer, kind, leftIcon, forceDisabled, customLabel } = props
  const toast = useToast()

  const context = useContext(BoxLockContext)
  const { open, interactive, setStatus, enableSuccessScreen, prefetch } = context

  // Only fetch experiment if this is a modal component and modal is open or prefetching
  const shouldFetchExperiment = context.componentType === 'modal' && (open || prefetch)

  // Get setEnableSuccessScreen from context (will be undefined for paywall)
  const setEnableSuccessScreen =
    'setEnableSuccessScreen' in context ? context.setEnableSuccessScreen : undefined

  const handleExperimentResult = useCallback(
    (enabled: boolean) => {
      if (setEnableSuccessScreen) {
        setEnableSuccessScreen(enabled)
      }
    },
    [setEnableSuccessScreen]
  )

  useOneClickSuccessExperiment({
    shouldFetch: shouldFetchExperiment,
    onResult: handleExperimentResult,
  })

  const { waitSubscriptionUpdate } = useWaitSubscriptionUpdate({ viewer })
  const [oneClickBuy] = useMutation<OneClickBuyMutation, OneClickBuyMutationVariables>(
    ONE_CLICK_BUY_MUTATION
  )
  const { sendFailureEvent, sendSuccessEvent, sendViewEvent, metricsRef } =
    useSendOneClickButtonEvents({
      offerLockCard,
      viewer,
    })

  useEffect(() => {
    if (open) {
      sendViewEvent()
    }
  }, [open, sendViewEvent])

  const { submitData } = offerLockCard
  const ccToken = offerLockCard.creditCard?.ccToken
  if (!ccToken) {
    return null
  }

  const showToast = (status: 'success' | 'error') => {
    const toastMessage = {
      success: 'Plano assinado com sucesso. Os novos recursos já estão disponíveis.',
      error: 'Erro ao processar pagamento. Tente novamente ou atualize o cartão.',
    }[status]
    return toast(toastMessage, {
      type: status === 'success' ? 'neutral' : 'negative',
    })
  }

  const onClick = async () => {
    if (!interactive) {
      return
    }

    setStatus('processing')

    let orderCode: string | null = null
    let success = false
    try {
      const input = { ccToken, submitData }
      const fetchResult = await oneClickBuy({ variables: { input } })

      const oneClickBuyData = fetchResult.data?.oneClickBuy
      if (oneClickBuyData) {
        ;({ orderCode, success } = oneClickBuyData)
      } else {
        throw new Error('unexpected no data')
      }
    } catch (err) {
      console.error('[shared-box-lock] OneClickBuyMutation', err)
    }

    let result = false
    let currentSubscription = null
    if (success && orderCode) {
      ;({ result, currentSubscription } = await waitSubscriptionUpdate(orderCode))

      const newStatus = result ? 'success' : 'error'
      setStatus(newStatus)
      if (result && enableSuccessScreen && context.componentType === 'modal') {
        context.setOneClickSuccessData({ ...offerLockCard, viewer })
      } else {
        showToast(newStatus)
      }
    } else {
      showToast('error')
      setStatus('error')
    }

    if (result) {
      sendSuccessEvent(currentSubscription)
    } else {
      sendFailureEvent()
    }
  }

  const cardWithCustomLabel = customLabel
    ? {
        ...offerLockCard,
        button: {
          ...offerLockCard.button,
          label: customLabel,
        },
      }
    : offerLockCard

  return (
    <SubscribeButton
      offerLockCard={cardWithCustomLabel}
      onClick={onClick}
      ref={metricsRef}
      kind={kind}
      leftIcon={leftIcon}
      forceDisabled={forceDisabled}
    />
  )
}

OneClickButton.fragments = {
  offerLockCard: gql`
    fragment OneClickButton_offerLockCard on OfferLockCard {
      offer {
        priceText
        frequency
        presentationName
        frequencyInDays
      }
      creditCard {
        ccToken
      }
      submitData {
        jwt
      }

      ...SubscribeButton_offerLockCard
      ...UseSendOneClickButtonEvents_offerLockCard
    }

    ${SubscribeButton.fragments.offerLockCard}
    ${useSendOneClickButtonEvents.fragments.offerLockCard}
  `,
  viewer: gql`
    fragment OneClickButton_viewer on Profile {
      ...useWaitSubscriptionUpdate_viewer
      ...UseSendOneClickButtonEvents_viewer
    }

    ${useWaitSubscriptionUpdate.fragments.viewer}
    ${useSendOneClickButtonEvents.fragments.viewer}
  `,
}
